import React, { useState } from 'react';
import { matchPath } from 'react-router-dom';
import { routes } from 'constants/config';
import { useFetch } from 'hooks';
import { Icon, Spinner } from 'ui';
import request from 'utils/request_ajax';

const match = () =>
  matchPath(window.location.pathname, { path: '/lessons/:lessonId' });

/**
 * Allows user to grant and revoke distribution access for a lesson to another
 * company within their organization.
 *
 * This component expects to be used on the lesson overview page.
 */
const ShareToCompany = () => {
  const lessonId = match()?.params?.lessonId;
  const [waiting, setWaiting] = useState(false);
  const {
    data: companies,
    loading: loadingCompanies,
    initialLoading: initialLoadingCompanies,
  } = useFetch(routes.ORGANIZATIONS.COMPANIES);
  const {
    data: distributors,
    loading: loadingDistributors,
    initialLoading: initialLoadingDistributors,
    runRequest: refreshDistributors,
  } = useFetch(routes.LESSON_OVERVIEW.DISTRIBUTORS_COMPANIES(lessonId));

  if (initialLoadingCompanies || initialLoadingDistributors) {
    return (
      <div className="d-f ai-c jc-c mb-8">
        <Spinner />
      </div>
    );
  }

  // No reason to render for single company organizations.
  if (!companies.companies.length) {
    return null;
  }

  const distributorIds = distributors.map(distributor => distributor.id);
  const filteredCompanyOptions = companies.companies.filter(
    company => !distributorIds.includes(company.id),
  );

  const syndicateToCompany = companyId => {
    setWaiting(true);
    request
      .postJSON(routes.LESSON_OVERVIEW.DISTRIBUTE_TO_COMPANY(lessonId), {
        company_id: companyId,
      })
      .then(() => {
        refreshDistributors();
        setWaiting(false);
      });
  };

  const revokeFromCompany = companyId => {
    setWaiting(true);
    request
      .postJSON(routes.LESSON_OVERVIEW.REVOKE_FROM_COMPANY(lessonId), {
        company_id: companyId,
      })
      .then(() => {
        refreshDistributors();
        setWaiting(false);
      });
  };

  return (
    <div className="border-top border-midnight-80 py-8">
      <h3 className="fz-12 fw-600 mt-0 mb-4">
        {t('share_settings.syndication.share_to')}
      </h3>

      <section id="syndication_list">
        {!waiting && loadingCompanies && (
          <div className="d-f ai-c jc-c mt-8">
            <Spinner />
          </div>
        )}

        <select
          className="w-fill form-control"
          onChange={e => {
            const companyId = e.target.value;
            if (companyId !== 'select_destination') {
              syndicateToCompany(companyId);
            }
          }}
        >
          <option value="select_destination">
            {t('share_settings.syndication.select_destination')}
          </option>
          {filteredCompanyOptions.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>

        <section>
          {!waiting && loadingDistributors && (
            <div className="d-f ai-c jc-c mt-8">
              <Spinner />
            </div>
          )}
          {distributors.map(company => (
            <article
              className="d-f jc-sb ai-c mt-8"
              id={`company_${company.id}`}
              key={company.id}
            >
              {company.name}

              <button
                type="button"
                onClick={() => revokeFromCompany(company.id)}
                className="u-resetBtn"
              >
                <Icon name="remove" />
              </button>
            </article>
          ))}
        </section>
      </section>
    </div>
  );
};

export default ShareToCompany;
